/**
 * Operações de manutenção e recuperação (PLAN §26).
 *
 * Princípio que organiza este arquivo: as ações são **graduadas**, da menos para a
 * mais destrutiva, e o reset total é claramente separado das operações normais
 * (F20-T10). O usuário nunca deve perder mídia e progresso tentando resolver um
 * problema de índice.
 */
import { db } from '@/db/schema';
import { clearCatalogOnly } from '@/db/repositories/catalog';
import { clearFinishedJobs, recoverInterruptedJobs } from '@/db/repositories/downloads';
import { logger } from '@/lib/logger';
import { getFileStore, reconcileStorage } from '@/storage/index';
import type { OfflineFileStore } from '@/storage/types';

export interface MaintenanceResult {
  label: string;
  detail: string;
}

/**
 * Nível 1 — reconciliar índice e arquivos (F20-T08).
 *
 * Não apaga nada além de temporários. Corrige o banco para refletir o que existe
 * de fato no filesystem.
 */
export async function rebuildLocalIndex(
  store: OfflineFileStore = getFileStore(),
): Promise<MaintenanceResult> {
  const report = await reconcileStorage(store);
  const recovered = await recoverInterruptedJobs();
  const cleared = await clearFinishedJobs();

  logger.info('manutenção', `índice reconstruído: ${JSON.stringify(report)}`);

  return {
    label: 'Índice reconstruído',
    detail:
      `${report.missingFiles.length} registro(s) corrigido(s), ` +
      `${report.untrackedFiles.length} arquivo(s) reaproveitado(s), ` +
      `${report.tempCleared} temporário(s) descartado(s), ` +
      `${recovered} download(s) devolvido(s) à fila, ` +
      `${cleared} job(s) concluído(s) removido(s) da lista. ` +
      'Nenhum vídeo foi apagado.',
  };
}

/**
 * Nível 2 — limpar somente o cache do app (F20-T09).
 *
 * Remove os caches do Service Worker e força novo download do shell. **Não toca**
 * em IndexedDB nem em OPFS: catálogo, progresso e vídeos permanecem.
 */
export async function clearAppCache(): Promise<MaintenanceResult> {
  let removed = 0;

  if (typeof caches !== 'undefined') {
    for (const name of await caches.keys()) {
      if (await caches.delete(name)) removed += 1;
    }
  }

  logger.info('manutenção', `${removed} cache(s) do app removido(s)`);

  return {
    label: 'Cache do app limpo',
    detail:
      `${removed} cache(s) removido(s). Recarregue com internet para baixar o app de novo. ` +
      'Catálogo, progresso e vídeos não foram afetados.',
  };
}

/**
 * Nível 3 — reconstruir o catálogo local (F20-T08).
 *
 * Apaga apenas as tabelas derivadas. O estado pessoal e a mídia permanecem, e a
 * próxima sincronização repopula tudo.
 */
export async function resetCatalogOnly(): Promise<MaintenanceResult> {
  await clearCatalogOnly();
  logger.info('manutenção', 'catálogo local descartado');

  return {
    label: 'Catálogo descartado',
    detail:
      'O catálogo será baixado de novo na próxima sincronização. ' +
      'Favoritos, progresso e vídeos foram preservados.',
  };
}

/**
 * Nível 4 — reset total (F20-T10, F20-T11).
 *
 * Apaga **tudo**: catálogo, estado pessoal, fila, sessões de estudo e vídeos.
 * Irreversível e sem backup — não há conta nem sincronização externa no MVP.
 * A confirmação é responsabilidade da interface.
 */
export async function factoryReset(
  store: OfflineFileStore = getFileStore(),
): Promise<MaintenanceResult> {
  const videos = await store.list().catch(() => [] as string[]);

  for (const key of videos) await store.remove(key).catch(() => undefined);
  await store.clearTemp().catch(() => 0);

  // Forma em array: a assinatura variádica do Dexie só aceita até 5 tabelas.
  await db.transaction(
    'rw',
    [
      db.categories,
      db.signs,
      db.catalogMetadata,
      db.localSignState,
      db.downloadQueue,
      db.studySessions,
      db.settings,
    ],
    async () => {
      await Promise.all([
        db.categories.clear(),
        db.signs.clear(),
        db.catalogMetadata.clear(),
        db.localSignState.clear(),
        db.downloadQueue.clear(),
        db.studySessions.clear(),
        db.settings.clear(),
      ]);
    },
  );

  if (typeof caches !== 'undefined') {
    for (const name of await caches.keys()) await caches.delete(name);
  }

  logger.warn('manutenção', `reset total executado; ${videos.length} vídeo(s) apagado(s)`);

  return {
    label: 'Aplicação restaurada',
    detail: `${videos.length} vídeo(s) e todos os dados locais foram apagados.`,
  };
}
